import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  HOME_COUNTRY,
  companySettingsOf,
  hasPermission,
  type Currency,
  type Permission,
  type Principal,
  type RoleKey,
} from '@fpc/shared';
import { api, apiClient, hasStoredSession } from '@/lib/api';
import { queryClient } from '@/lib/queryClient';

interface AuthState {
  user: Principal | null;
  loading: boolean;
  login(email: string, password: string): Promise<void>;
  logout(): Promise<void>;
  /** True when the current user holds the permission. */
  can(permission: Permission): boolean;
  canAny(...permissions: Permission[]): boolean;
  hasRole(role: RoleKey): boolean;
  /** The company the user is currently working in. */
  companyId: string | undefined;
  setCompanyId(id: string): void;
  /**
   * The vertical the user has narrowed to, if any.
   *
   * A view filter, not a permission: the server already restricts a scoped
   * user to their own verticals, and refuses one they were not granted. This
   * lets someone with several pick a lens to work through.
   */
  verticalId: string | undefined;
  setVerticalId(id: string | undefined): void;
  /**
   * The selected company's money: the one currency its invoices, payments and
   * statements are in. Defaults to INR until the company has loaded.
   */
  company: CompanyMoney;
  /** The common "View in" choice: amounts in their own currency, or as rupees. */
  viewIn: ViewIn;
  setViewIn(mode: ViewIn): void;
  /**
   * The latest rupee rates, fetched live only while "View in INR" is on and
   * never stored. Undefined until they arrive, or when they cannot be had.
   */
  inrRates?: LiveInrRates;
}

export type ViewIn = 'NATIVE' | 'INR';

export interface LiveInrRates {
  /** Rupees per one unit of each currency. */
  rates: Record<string, number>;
  asOf: string;
  source: string;
  /** The provider could not be reached; these are the last rates it gave. */
  stale: boolean;
}

export interface CompanyMoney {
  currency: Currency;
  country: string;
  /** India-only rules (GSTIN, PAN, IFSC, TDS) apply. */
  india: boolean;
  /** TDS is calculated on invoices: Indian, and switched on in System Settings. */
  tds: boolean;
  /** The company's own GSTIN: the buyer's on every invoice it receives. */
  gstin?: string;
}

const HOME_MONEY: CompanyMoney = {
  currency: 'INR',
  country: HOME_COUNTRY,
  india: true,
  tds: false,
};

const AuthContext = createContext<AuthState | null>(null);
const COMPANY_KEY = 'fpc.companyId';
const VERTICAL_KEY = 'fpc.verticalId';
const VIEW_IN_KEY = 'fpc.viewIn';

/** How long, and how often, a session is re-checked while the API cannot be reached. */
export const RESTORE_RETRY_FOR_MS = 60_000;
export const RESTORE_RETRY_EVERY_MS = 2_000;

/**
 * The API did not answer for itself: the request never reached it (fetch
 * threw), or the dev proxy / gateway in front of it failed (5xx). A 401 or
 * 403 is the API speaking, and does end the session.
 */
function apiUnreachable(error: unknown): boolean {
  if (error instanceof TypeError) return true;
  const status = (error as { status?: number } | null)?.status;
  return status === undefined || status >= 500;
}

function storedViewIn(): ViewIn {
  try {
    return localStorage.getItem(VIEW_IN_KEY) === 'INR' ? 'INR' : 'NATIVE';
  } catch {
    return 'NATIVE';
  }
}

/**
 * Keeps the current company if it still belongs to this principal, otherwise
 * falls back to their first one. Plain `current ?? companyIds[0]` only fills
 * a gap -- it happily keeps a *different* account's company id around, since
 * logging in as someone new never clears the previous session's storage.
 *
 * An empty `companyIds` is not "no companies" -- per the OrgScope convention
 * (packages/shared/src/types.ts), it means "unrestricted", the case for
 * platform and company admins. `companyIds[0]` is `undefined` for such an
 * account, so without this, `companyId` never resolves at all: every
 * company-scoped write (e.g. uploading an invoice) stays permanently
 * disabled for that account until someone happens to open the sidebar's
 * company switcher. Falls back to the tenant's first company -- the same
 * list that switcher already queries -- so an unrestricted admin gets a
 * working default instead of a silently broken one.
 */
async function resolveCompanyId(
  current: string | undefined,
  companyIds: string[],
): Promise<string | undefined> {
  const unrestricted = companyIds.length === 0;
  // Unrestricted means *any* company is valid, so a prior choice (made via
  // the switcher, or from an earlier session) stays valid indefinitely --
  // this must not reset to the tenant's first company on every login.
  if (current && (unrestricted || companyIds.includes(current))) return current;
  if (!unrestricted) return companyIds[0];

  try {
    const page = await api.settings.companies({ pageSize: 1 });
    return page.items[0]?.id;
  } catch {
    return undefined;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<Principal | null>(null);
  const [loading, setLoading] = useState(hasStoredSession());
  const [companyId, setCompanyIdState] = useState<string | undefined>(
    () => localStorage.getItem(COMPANY_KEY) ?? undefined,
  );
  const [verticalId, setVerticalIdState] = useState<string | undefined>(
    () => localStorage.getItem(VERTICAL_KEY) ?? undefined,
  );
  const [viewIn, setViewInState] = useState<ViewIn>(storedViewIn);

  // Keyed under the Companies screen's key, so saving a company there refreshes it.
  const companyQuery = useQuery(
    {
      queryKey: ['settings-companies', companyId, 'money'],
      queryFn: () => api.settings.company(companyId!),
      // A vendor login has no company screen to read; its amounts carry their
      // own currency, so it simply keeps the home default.
      enabled: !!user && !!companyId && user.permissions.includes('company:read'),
      staleTime: 5 * 60_000,
    },
    // The app's one client, passed directly: the provider sits above this one
    // in the app, but tests render AuthProvider on its own.
    queryClient,
  );
  const company = useMemo<CompanyMoney>(() => {
    const data = companyQuery.data;
    if (!data) return HOME_MONEY;
    const country = data.country ?? HOME_COUNTRY;
    const india = country === HOME_COUNTRY;
    return {
      currency: data.baseCurrency ?? 'INR',
      country,
      india,
      tds: india && companySettingsOf(data).tdsEnabled,
      gstin: data.gstin || undefined,
    };
  }, [companyQuery.data]);

  // Fetched live, and only when someone asks to see rupees: the server holds
  // one answer for everyone for a short while, and nothing is ever saved.
  const ratesQuery = useQuery(
    {
      queryKey: ['fx', 'inr-rates'],
      queryFn: () => api.fx.inrRates(),
      enabled: !!user && viewIn === 'INR' && company.currency !== 'INR',
      staleTime: 15 * 60_000,
      refetchInterval: 30 * 60_000,
      retry: 1,
    },
    queryClient,
  );
  const inrRates = ratesQuery.data;

  // Restore the session on load. Permissions come from the server rather than
  // the token, so a role change takes effect on the next page load.
  //
  // An API that cannot be reached (still starting, restarting, a network blip)
  // says nothing about the session, so that is retried for a while rather than
  // treated as signed out; only an answer from the API ends the session.
  useEffect(() => {
    if (!hasStoredSession()) {
      setLoading(false);
      return;
    }
    let cancelled = false;

    const restore = async () => {
      const giveUpAt = Date.now() + RESTORE_RETRY_FOR_MS;
      for (;;) {
        try {
          const principal = await api.auth.me();
          if (cancelled) return;
          setUser(principal);
          const resolved = await resolveCompanyId(
            localStorage.getItem(COMPANY_KEY) ?? undefined,
            principal.companyIds,
          );
          if (resolved) localStorage.setItem(COMPANY_KEY, resolved);
          setCompanyIdState(resolved);
          return;
        } catch (error) {
          if (cancelled) return;
          if (!apiUnreachable(error) || Date.now() >= giveUpAt) {
            setUser(null);
            return;
          }
          await new Promise((resolve) => setTimeout(resolve, RESTORE_RETRY_EVERY_MS));
          if (cancelled) return;
        }
      }
    };

    void restore().finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const result = await apiClient.login(email, password);
    // Nothing fetched for a previous account may be shown to this one.
    queryClient.clear();
    setUser(result.user);
    const resolved = await resolveCompanyId(
      localStorage.getItem(COMPANY_KEY) ?? undefined,
      result.user.companyIds,
    );
    if (resolved) localStorage.setItem(COMPANY_KEY, resolved);
    setCompanyIdState(resolved);
  }, []);

  const logout = useCallback(async () => {
    await apiClient.logout().catch(() => undefined);
    setUser(null);
    queryClient.clear();
    // Otherwise the next person to sign in on this browser silently inherits
    // this account's company/vertical -- valid-looking, but scoped wrong.
    localStorage.removeItem(COMPANY_KEY);
    localStorage.removeItem(VERTICAL_KEY);
    setCompanyIdState(undefined);
    setVerticalIdState(undefined);
  }, []);

  const setCompanyId = useCallback((id: string) => {
    localStorage.setItem(COMPANY_KEY, id);
    setCompanyIdState(id);
    // Verticals belong to a company, so a stale one would filter everything
    // away after a switch.
    localStorage.removeItem(VERTICAL_KEY);
    setVerticalIdState(undefined);
  }, []);

  const setVerticalId = useCallback((id: string | undefined) => {
    if (id) localStorage.setItem(VERTICAL_KEY, id);
    else localStorage.removeItem(VERTICAL_KEY);
    setVerticalIdState(id);
  }, []);

  const setViewIn = useCallback((mode: ViewIn) => {
    try {
      localStorage.setItem(VIEW_IN_KEY, mode);
    } catch {
      // A private window still switches for this session.
    }
    setViewInState(mode);
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      user,
      loading,
      login,
      logout,
      can: (permission) => hasPermission(user?.permissions, permission),
      canAny: (...permissions) =>
        permissions.some((entry) => hasPermission(user?.permissions, entry)),
      hasRole: (role) => user?.roleKey === role,
      companyId,
      setCompanyId,
      verticalId,
      setVerticalId,
      company,
      viewIn,
      setViewIn,
      inrRates,
    }),
    [
      user,
      loading,
      login,
      logout,
      companyId,
      setCompanyId,
      verticalId,
      setVerticalId,
      company,
      viewIn,
      setViewIn,
      inrRates,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside AuthProvider');
  return context;
}

/** Convenience for the very common single-permission check. */
export function usePermission(permission: Permission): boolean {
  return useAuth().can(permission);
}
